import React, { useMemo, useState } from 'react';
import { Button, Space } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { ESTADO_COLORS, formatHora } from './agendaConstants';

dayjs.extend(isoWeek);

const HOUR_START = 7;
const HOUR_END = 20;
const SLOT_HEIGHT = 48;
const HOURS = Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, i) => HOUR_START + i);

const parseMinutes = (hora) => {
  if (!hora) return HOUR_START * 60;
  const [h, m] = String(hora).slice(0, 5).split(':').map(Number);
  return h * 60 + (m || 0);
};

const AgendaCalendarioSemanal = ({ reuniones, currentDate, onDateChange, onSelectReunion, onMover }) => {
  const [draggingId, setDraggingId] = useState(null);

  const weekStart = currentDate.startOf('isoWeek');
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day')),
    [weekStart]
  );

  const byDay = useMemo(() => {
    const map = {};
    days.forEach((d) => {
      map[d.format('YYYY-MM-DD')] = [];
    });
    reuniones.forEach((r) => {
      const key = String(r.fecha).slice(0, 10);
      if (map[key]) map[key].push(r);
    });
    return map;
  }, [reuniones, days]);

  const handleDrop = (dayKey, hour) => {
    if (!draggingId) return;
    const reunion = reuniones.find((r) => r.id === draggingId);
    if (!reunion) return;

    const startMin = parseMinutes(reunion.horaInicio);
    const endMin = parseMinutes(reunion.horaFin);
    const duration = Math.max(endMin - startMin, 60);

    const newStartMin = hour * 60;
    const newEndMin = newStartMin + duration;

    const fmt = (mins) => {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    };

    onMover(reunion, {
      fecha: dayKey,
      horaInicio: fmt(newStartMin),
      horaFin: fmt(newEndMin),
    });
    setDraggingId(null);
  };

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button
          icon={<LeftOutlined />}
          size="small"
          onClick={() => onDateChange(currentDate.subtract(1, 'week'))}
        />
        <span style={{ fontWeight: 600, color: '#198039' }}>
          Semana del {weekStart.format('DD/MM/YYYY')} al {weekStart.add(6, 'day').format('DD/MM/YYYY')}
        </span>
        <Button
          icon={<RightOutlined />}
          size="small"
          onClick={() => onDateChange(currentDate.add(1, 'week'))}
        />
        <Button size="small" onClick={() => onDateChange(dayjs())}>
          Hoy
        </Button>
      </Space>

      <div style={{ overflowX: 'auto', border: '1px solid #d8e8dc', borderRadius: 6, background: '#fff' }}>
        <div style={{ display: 'flex', minWidth: 720 }}>
          <div style={{ width: 52, flexShrink: 0, borderRight: '1px solid #e8e8e8' }}>
            <div style={{ height: 36, borderBottom: '1px solid #e8e8e8' }} />
            {HOURS.map((h) => (
              <div
                key={h}
                style={{
                  height: SLOT_HEIGHT,
                  fontSize: 10,
                  color: '#888',
                  textAlign: 'right',
                  paddingRight: 4,
                  borderBottom: '1px solid #f0f0f0',
                  lineHeight: `${SLOT_HEIGHT}px`,
                }}
              >
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {days.map((day) => {
            const dayKey = day.format('YYYY-MM-DD');
            const dayItems = byDay[dayKey] || [];
            const isToday = day.isSame(dayjs(), 'day');

            return (
              <div key={dayKey} style={{ flex: 1, minWidth: 90, borderRight: '1px solid #e8e8e8' }}>
                <div
                  style={{
                    height: 36,
                    textAlign: 'center',
                    fontWeight: isToday ? 700 : 500,
                    fontSize: 12,
                    color: isToday ? '#198039' : '#333',
                    borderBottom: '1px solid #e8e8e8',
                    lineHeight: '36px',
                    background: isToday ? 'rgba(25,128,57,0.06)' : undefined,
                  }}
                >
                  {day.format('ddd DD')}
                </div>
                <div style={{ position: 'relative' }}>
                  {HOURS.map((h) => (
                    <div
                      key={h}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => handleDrop(dayKey, h)}
                      style={{
                        height: SLOT_HEIGHT,
                        borderBottom: '1px solid #f0f0f0',
                        background: draggingId ? 'rgba(25,128,57,0.03)' : undefined,
                      }}
                    />
                  ))}
                  {dayItems.map((r) => {
                    const top = ((parseMinutes(r.horaInicio) - HOUR_START * 60) / 60) * SLOT_HEIGHT;
                    const height = Math.max(
                      ((parseMinutes(r.horaFin) - parseMinutes(r.horaInicio)) / 60) * SLOT_HEIGHT,
                      SLOT_HEIGHT * 0.6
                    );
                    if (top < 0) return null;

                    return (
                      <div
                        key={r.id}
                        draggable
                        onDragStart={() => setDraggingId(r.id)}
                        onDragEnd={() => setDraggingId(null)}
                        onClick={() => onSelectReunion(r)}
                        title={`${r.cliente} — ${formatHora(r.horaInicio)}`}
                        style={{
                          position: 'absolute',
                          top,
                          left: 2,
                          right: 2,
                          height,
                          background: ESTADO_COLORS[r.estado] || '#1677ff',
                          color: '#fff',
                          fontSize: 10,
                          padding: '2px 4px',
                          borderRadius: 4,
                          overflow: 'hidden',
                          cursor: 'grab',
                          zIndex: 2,
                          opacity: draggingId === r.id ? 0.6 : 1,
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>{formatHora(r.horaInicio)}</div>
                        <div>{r.cliente}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
        Arrastrá una reunión a otro horario o día para reprogramarla.
      </div>
    </div>
  );
};

export default AgendaCalendarioSemanal;
